import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { getFilterColor, getFilterFillColor } from '../../../utils/filter-colors.util';
import { TranslationService } from '../../../services/translation.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';

interface FilterData {
  key: string;
  name: string;
  raw_score: number;
  ui_score: number;
  output_mask_name: string;
  subFilters?: SubFilterData[];
}

interface SubFilterData {
  key: string;
  name: string;
  raw_score: number;
  ui_score: number;
  output_mask_name: string;
}

@Component({
  selector: 'app-skin-filter-viewer',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './skin-filter-viewer.component.html',
  styleUrl: './skin-filter-viewer.component.scss',
})
export class SkinFilterViewerComponent implements OnInit, AfterViewInit {
  @Input() photoUrl: string = '/assets/photo.jpeg';
  @Input() filterData: any = null;
  @Input() maskImages: { [key: string]: string } = {};
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  activeFilter: string = '';
  activeSubFilter: string = '';
  filters: FilterData[] = [];
  subFilters: SubFilterData[] = [];
  isSubFilterExpanded: boolean = false;

  // Drag functionality properties
  isDragging = false;
  private startX = 0;
  private dragScrollLeft = 0;
  private initialTouchX = 0;
  canScrollLeft = false;
  canScrollRight = false;

  constructor(private translationService: TranslationService) {}

  ngOnInit() {
    console.log(
      'SkinFilterViewerComponent: photoUrl received:',
      this.photoUrl?.substring(0, 50)
    );
    console.log(
      'SkinFilterViewerComponent: filterData keys:',
      Object.keys(this.filterData || {})
    );
    this.initializeFilters();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.updateScrollButtons();
    }, 100);
  }

  initializeFilters() {
    const filterMapping = {
      hd_acne: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_acne'
      ),
      hd_wrinkle: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_wrinkle'
      ),
      hd_pore: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_pore'
      ),
      hd_moisture: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_moisture'
      ),
      hd_redness: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_redness'
      ),
      hd_age_spot: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_age_spot'
      ),
      hd_dark_circle: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_dark_circle'
      ),
      hd_firmness: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_firmness'
      ),
      hd_texture: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_texture'
      ),
      hd_radiance: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_radiance'
      ),
      hd_oiliness: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_oiliness'
      ),
      hd_eye_bag: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_eye_bag'
      ),
      hd_droopy_upper_eyelid: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_droopy_upper_eyelid'
      ),
      hd_droopy_lower_eyelid: this.translationService.translate(
        'results.skinFilterViewer.filters.hd_droopy_lower_eyelid'
      ),
    };

    const subFilterMapping = {
      forehead: this.translationService.translate(
        'results.skinFilterViewer.subFilters.forehead'
      ),
      glabellar: this.translationService.translate(
        'results.skinFilterViewer.subFilters.glabellar'
      ),
      crowfeet: this.translationService.translate(
        'results.skinFilterViewer.subFilters.crowfeet'
      ),
      periocular: this.translationService.translate(
        'results.skinFilterViewer.subFilters.periocular'
      ),
      nasolabial: this.translationService.translate(
        'results.skinFilterViewer.subFilters.nasolabial'
      ),
      marionette: this.translationService.translate(
        'results.skinFilterViewer.subFilters.marionette'
      ),
      whole: this.translationService.translate(
        'results.skinFilterViewer.subFilters.whole'
      ),
      nose: this.translationService.translate(
        'results.skinFilterViewer.subFilters.nose'
      ),
      cheek: this.translationService.translate(
        'results.skinFilterViewer.subFilters.cheek'
      ),
    };

    // Define the desired order of filters
    const filterOrder = [
      'hd_acne', // 1st - Acne
      'hd_pore', // 2nd - Pores
      'hd_wrinkle', // 3rd - Wrinkles
      'hd_moisture', // 4th - Moisture
      'hd_redness', // 5th - Redness
      'hd_age_spot', // 6th - Age Spots
      'hd_dark_circle', // 7th - Dark Circles
      'hd_firmness', // 8th - Firmness
      'hd_texture', // 9th - Texture
      'hd_radiance', // 10th - Radiance
      'hd_oiliness', // 11th - Oiliness
      'hd_eye_bag', // 12th - Eye Bags
      'hd_droopy_upper_eyelid', // 13th - Upper Eyelid
      'hd_droopy_lower_eyelid', // 14th - Lower Eyelid
    ];

    if (this.filterData) {
      const availableKeys = Object.keys(this.filterData).filter(
        (key) => key !== 'all' && key !== 'skin_age' && filterMapping[key]
      );

      // Sort keys according to the defined order
      const orderedKeys = filterOrder.filter((key) =>
        availableKeys.includes(key)
      );

      this.filters = orderedKeys.map((key) => {
        const filterItem = this.filterData[key];

        // Handle nested structures - prefer 'whole' if available, otherwise use the direct values
        let scoreData;
        let subFilters: SubFilterData[] = [];

        // Check if this filter has sub-regions (wrinkles or pores)
        if (key === 'hd_wrinkle' || key === 'hd_pore') {
          // Extract sub-filters from nested structure - prioritize "whole" (All) first
          const subKeys = Object.keys(filterItem);

          // Sort to put "whole" first, then others
          const sortedSubKeys = subKeys.sort((a, b) => {
            if (a === 'whole') return -1;
            if (b === 'whole') return 1;
            return 0;
          });

          sortedSubKeys.forEach((subKey) => {
            if (subFilterMapping[subKey] && filterItem[subKey]) {
              subFilters.push({
                key: subKey,
                name:
                  key === 'hd_pore' && subKey === 'whole'
                    ? this.translationService.translate(
                        'results.skinFilterViewer.subFilters.wholeAreas'
                      )
                    : subFilterMapping[subKey],
                raw_score: filterItem[subKey].raw_score,
                ui_score: filterItem[subKey].ui_score,
                output_mask_name: filterItem[subKey].output_mask_name,
              });
            }
          });

          // Use 'whole' as the default display data for the main filter
          scoreData = filterItem.whole || filterItem;
        } else if (filterItem.whole) {
          scoreData = filterItem.whole;
        } else if (
          typeof filterItem === 'object' &&
          filterItem.raw_score !== undefined
        ) {
          scoreData = filterItem;
        } else {
          // For complex nested structures like hd_pore or hd_wrinkle, use 'whole' as default
          scoreData = filterItem.whole || filterItem;
        }

        return {
          key,
          name: filterMapping[key],
          raw_score: scoreData.raw_score,
          ui_score: scoreData.ui_score,
          output_mask_name: scoreData.output_mask_name,
          subFilters: subFilters.length > 0 ? subFilters : undefined,
        };
      });

      this.activeFilter = this.filters.length > 0 ? this.filters[0].key : '';
      this.updateSubFilters();
    }
  }

  // loadDefaultFilters() {
  //   console.log(
  //     'SkinFilterViewerComponent: loadDefaultFilters - loading default data'
  //   );

  //   this.filters = [
  //     {
  //       key: 'acne',
  //       name: 'Acne',
  //       raw_score: 100,
  //       ui_score: 88,
  //       output_mask_name: 'acne_output.png',
  //     },
  //     {
  //       key: 'wrinkle',
  //       name: 'Wrinkles',
  //       raw_score: 85.2,
  //       ui_score: 89,
  //       output_mask_name: 'wrinkle_output.png',
  //     },
  //     {
  //       key: 'pore',
  //       name: 'Pores',
  //       raw_score: 99.9,
  //       ui_score: 90,
  //       output_mask_name: 'pore_output.png',
  //     },
  //     {
  //       key: 'moisture',
  //       name: 'Moisture',
  //       raw_score: 80.1,
  //       ui_score: 83,
  //       output_mask_name: 'moisture_output.png',
  //     },
  //     {
  //       key: 'redness',
  //       name: 'Redness',
  //       raw_score: 33.5,
  //       ui_score: 60,
  //       output_mask_name: 'redness_output.png',
  //     },
  //   ];

  //   this.activeFilter = 'acne';
  // }

  selectFilter(filterKey: string) {
    this.activeFilter = filterKey;
    this.updateSubFilters();

    // if the filter has sub-filters, expand the sub-filter controls
    if (this.hasSubFilters()) {
      this.isSubFilterExpanded = true;
    }
  }

  selectSubFilter(subFilterKey: string) {
    this.activeSubFilter = subFilterKey;
  }

  updateSubFilters() {
    const activeFilter = this.filters.find((f) => f.key === this.activeFilter);
    if (activeFilter && activeFilter.subFilters) {
      this.subFilters = activeFilter.subFilters;
      this.activeSubFilter =
        this.subFilters.length > 0 ? this.subFilters[0].key : '';
    } else {
      this.subFilters = [];
      this.activeSubFilter = '';
    }
  }

  hasSubFilters(): boolean {
    const activeFilter = this.filters.find((f) => f.key === this.activeFilter);
    return activeFilter?.subFilters && activeFilter.subFilters.length > 0;
  }

  toggleSubFilters(): void {
    this.isSubFilterExpanded = !this.isSubFilterExpanded;
  }

  getActiveFilterData(): FilterData | null {
    const activeFilterData = this.filters.find(
      (f) => f.key === this.activeFilter
    );

    // If there's an active sub-filter, return the sub-filter data
    if (this.activeSubFilter && activeFilterData?.subFilters) {
      const subFilterData = activeFilterData.subFilters.find(
        (sf) => sf.key === this.activeSubFilter
      );
      if (subFilterData) {
        return {
          key: activeFilterData.key,
          name: activeFilterData.name,
          raw_score: subFilterData.raw_score,
          ui_score: subFilterData.ui_score,
          output_mask_name: subFilterData.output_mask_name,
        };
      }
    }

    return activeFilterData || null;
  }

  getFilterMaskUrl(maskName: string): string {
    if (this.maskImages && this.maskImages[maskName]) {
      return this.maskImages[maskName];
    }

    const fallbackUrl = `/assets/skinanalysisResult/${maskName}`;
    return fallbackUrl;
  }

  getFilterColor(filterKey: string): string {
    return getFilterColor(filterKey);
  }

  getFilterFillColor(filterKey: string): string {
    return getFilterFillColor(filterKey);
  }

  // Mouse drag events
  onDragStart(event: MouseEvent) {
    this.isDragging = true;
    this.startX = event.pageX - this.scrollContainer.nativeElement.offsetLeft;
    this.dragScrollLeft = this.scrollContainer.nativeElement.scrollLeft;
    event.preventDefault();
  }

  onDragMove(event: MouseEvent) {
    if (!this.isDragging) return;
    event.preventDefault();
    const x = event.pageX - this.scrollContainer.nativeElement.offsetLeft;
    const walk = x - this.startX;
    this.scrollContainer.nativeElement.scrollLeft = this.dragScrollLeft - walk;
  }

  onDragEnd() {
    if (this.isDragging) {
    }
    this.isDragging = false;
  }

  // Touch events for mobile
  onTouchStart(event: TouchEvent) {
    this.isDragging = true;
    this.initialTouchX = event.touches[0].clientX;
    this.dragScrollLeft = this.scrollContainer.nativeElement.scrollLeft;
  }

  onTouchMove(event: TouchEvent) {
    if (!this.isDragging) return;
    const currentTouchX = event.touches[0].clientX;
    const deltaX = this.initialTouchX - currentTouchX;
    this.scrollContainer.nativeElement.scrollLeft =
      this.dragScrollLeft + deltaX;
  }

  onTouchEnd() {
    if (this.isDragging) {
    }
    this.isDragging = false;
  }

  getFilterDescription(filterKey: string): string {
    const descriptionKey = `results.skinFilterViewer.descriptions.${filterKey}`;
    const description = this.translationService.translate(descriptionKey);

    if (description && description !== descriptionKey) {
      return description;
    }

    return this.translationService.translate(
      'results.skinFilterViewer.descriptions.default',
      {
        filter: this.getFilterName(filterKey).toLowerCase(),
      }
    );
  }

  private getFilterName(filterKey: string): string {
    const filterNameKey = `results.skinFilterViewer.filters.${filterKey}`;
    const filterName = this.translationService.translate(filterNameKey);

    if (filterName && filterName !== filterNameKey) {
      return filterName;
    }

    return this.translationService.translate(
      'results.skinFilterViewer.analysis'
    );
  }

  private isRTL(): boolean {
    if (this.scrollContainer) {
      return getComputedStyle(this.scrollContainer.nativeElement).direction === 'rtl';
    }
    return document.documentElement.getAttribute('dir') === 'rtl';
  }

  scrollLeft() {
    if (this.scrollContainer) {
      const container = this.scrollContainer.nativeElement;
      container.scrollBy({ left: -200, behavior: 'smooth' });
    }
  }

  scrollRight() {
    if (this.scrollContainer) {
      const container = this.scrollContainer.nativeElement;
      container.scrollBy({ left: 200, behavior: 'smooth' });
    }
  }

  onScroll() {
    this.updateScrollButtons();
  }

  updateScrollButtons() {
    if (this.scrollContainer) {
      const container = this.scrollContainer.nativeElement;
      const maxScroll = container.scrollWidth - container.clientWidth;

      if (this.isRTL()) {
        // RTL: scrollLeft is 0 at start (right edge), goes negative toward end (left edge)
        const absScroll = Math.abs(container.scrollLeft);
        this.canScrollLeft = absScroll < maxScroll - 1;
        this.canScrollRight = absScroll > 1;
      } else {
        // LTR: scrollLeft is 0 at start (left edge), goes positive toward end (right edge)
        this.canScrollLeft = container.scrollLeft > 1;
        this.canScrollRight = container.scrollLeft < maxScroll - 1;
      }
    }
  }
}
